"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { getProductById, updateProduct, ProductItem, deleteImageFromStorage } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Package } from '@/components/icons';
import { useSession } from '@/context/SessionContext';
import ProductForm from '@/components/ProductForm'; // Import ProductForm
import { Skeleton } from '@/components/ui/skeleton'; // Ensure Skeleton is imported

interface EditProductPageProps {
  session: Session | null;
}

const EditProductPage: React.FC<EditProductPageProps> = ({ session }) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useSession();

  const [product, setProduct] = useState<ProductItem | null>(null);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [loadingSubmission, setLoadingSubmission] = useState(false); // Renamed 'loading' to 'loadingSubmission' for clarity

  const currentUserId = session?.user?.id;


  // Log mount and unmount
  useEffect(() => {
    return () => {
    };
  }, [productId]);

  const fetchProductData = useCallback(async () => {
    if (!productId) {
      console.error("EditProductPage ERROR: Product ID not provided in URL.");
      toast({ title: "Error", description: "ID de producto no proporcionado.", variant: "destructive" });
      navigate('/tiendas');
      return;
    }
    setLoadingInitialData(true);
    try {
      const fetchedProduct = await getProductById(productId);

      if (!fetchedProduct) {
        console.warn("EditProductPage WARN: Product not found for ID:", productId);
        toast({ title: "Producto no encontrado", description: "El producto que intentas editar no existe.", variant: "destructive" });
        navigate('/tiendas');
        return;
      }

      if (fetchedProduct.seller_id !== currentUserId) {
        console.warn("EditProductPage WARN: Access denied. User is not the seller. Product seller:", fetchedProduct.seller_id, "Current user:", currentUserId);
        toast({ title: "Acceso denegado", description: "No tienes permiso para editar este producto.", variant: "destructive" });
        navigate('/tiendas');
        return;
      }
      setProduct(fetchedProduct);
    } catch (error) {
      console.error("EditProductPage ERROR: Error fetching product for edit:", error);
      toast({ title: "Error", description: "No se pudo cargar el producto para editar. Inténtalo de nuevo.", variant: "destructive" });
      navigate('/tiendas');
    } finally {
      setLoadingInitialData(false);
    }
  }, [productId, currentUserId, navigate, toast]);

  useEffect(() => {
    if (session?.user?.id && productId) {
      fetchProductData();
    } else if (!session && !loadingInitialData) {
      navigate('/');
    }
  }, [session, productId, fetchProductData, navigate]); // Removed loadingInitialData from dependencies

  const handleSubmit = useCallback(async (
    productData: Omit<ProductItem, 'id' | 'created_at' | 'updated_at' | 'seller_id' | 'profiles' | 'current_bid' | 'highest_bidder_id'>,
    imagesToDelete: string[]
  ) => {
    if (!currentUserId || !product) {
      console.error("EditProductPage ERROR: Current user or product not available for submission.");
      toast({ title: "Error", description: "Debes iniciar sesión para actualizar un producto.", variant: "destructive" });
      return;
    }

    setLoadingSubmission(true);
    try {
      const updatedFields = {
        ...productData,
        current_bid: productData.is_auction ? (product.current_bid || productData.starting_bid) : null,
      };
      await updateProduct(product.id, currentUserId, updatedFields);

      // Note: for groceries we persist price/stock directly on `products` (no product_availability upsert)

      // Delete images from storage that were removed from the product
      for (const url of imagesToDelete) {
        await deleteImageFromStorage(url);
      }

      toast({
        title: "Producto actualizado",
        description: "Tu producto ha sido actualizado exitosamente.",
      });

      const targetUrl = `/products/${product.id}`;
      navigate(targetUrl, { replace: true });
      
      // Fallback para PWA si el router no responde
      setTimeout(() => {
        if (window.location.pathname !== targetUrl) {
          window.location.href = targetUrl;
        }
      }, 100);
      
    } catch (error) {
      console.error("EditProductPage ERROR: Error updating product:", error);
      toast({
        title: "Error al actualizar producto",
        description: "No se pudo actualizar el producto. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoadingSubmission(false);
    }
  }, [currentUserId, product, navigate, toast]);

  if (loadingInitialData) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-4 md:p-6 text-center text-muted-foreground">
        <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
        <p>El producto que intentas editar no existe o no tienes permiso para verlo.</p>
        <button onClick={() => navigate('/tiendas')} className="text-info hover:text-info/80 underline mt-4 block">
          Volver a Tiendas
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-2">
        <Package className="h-6 w-6" /> Editar Producto: {product.title}
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Detalles del Producto</CardTitle>
          <CardDescription>
            Modifica la información de tu producto.
          </CardDescription>
        </CardHeader>
        <ProductForm
          session={session}
          profile={profile}
          initialProduct={product}
          onSubmit={handleSubmit}
          loading={loadingSubmission}
          submitButtonText="Guardar Cambios"
        />
      </Card>
    </div>
  );
};

export default EditProductPage;